<template>
  <div class="cycle-task-container">
    <!-- 搜索和操作区域 -->
    <div class="operation-area">
      <el-input v-model="searchText" placeholder="搜索任务标题" style="width: 200px; margin-right: 10px" clearable />
      <el-select v-model="selectedTypes" multiple placeholder="筛选任务类型" style="width: 200px; margin-right: 10px" clearable>
        <el-option v-for="item in taskTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-select v-model="selectedStatus" placeholder="筛选任务状态" style="width: 270px; margin-right: 10px" multiple clearable @clear="selectedStatus = []">
        <el-option label="运行中" value="running" />
        <el-option label="已关闭" value="closed" />
        <el-option label="已停止" value="stopped" />
      </el-select>
      <el-button type="primary" @click="handleCreate">新建任务</el-button>
    </div>

    <!-- 任务列表表格 -->
    <el-table :data="filteredTaskList" border style="width: 100%" @sort-change="handleSortChange">
      <el-table-column prop="id" label="ID" width="80" sortable="custom" />
      <el-table-column prop="title" label="任务标题" width="200" sortable="custom">
        <template #default="{row}">
          <el-tooltip :content="row.title" placement="top">
            <span class="text-ellipsis">{{ row.title }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="type" label="任务类型" width="180" sortable="custom">
        <template #default="{row}">
          {{ taskTypeMap[row.type] || row.type }}
        </template>
      </el-table-column>
      <el-table-column prop="cron_cycle_type" label="运行周期" width="120" sortable="custom">
        <template #default="{row}">
          {{ formatCycleType(row.cron_cycle_type) }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100" sortable="custom" :sort-by="(row) => isTaskRunning(row) ? 1 : 0">
        <template #default="{row}">
          <el-tag :type="getStatusTagType(row)">
            {{ getStatusText(row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="start_date" label="开始时间" width="120" sortable="custom" />
      <el-table-column prop="end_date" label="结束时间" width="120" sortable="custom" />
      <el-table-column prop="add_time" label="创建时间" width="160" sortable="custom" />
      <!-- <el-table-column prop="edit_time" label="修改时间" width="160" /> -->
      <el-table-column label="到期告警" width="140" align="center" sortable="custom" :sort-orders="['ascending', 'descending']" :sort-by="(row) => getDaysToExpire(row)" prop="expire_days">
        <template #default="{row}">
          <template v-if="isTaskRunning(row) && !['close', '2'].includes(row.status)">
            <el-tag :type="getExpireTagType(row)">
              {{ getExpireText(row) }}
            </el-tag>
          </template>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="360">
        <template #default="{row}">
          <el-button class="report-button" type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button class="report-button" size="small" :type="isTaskRunning(row) ? 'danger' : 'success'" @click="isTaskRunning(row) ? handleStop(row) : handleStart(row)" :disabled="isTaskRunning(row) ? false : !canStartTask(row)">
            {{ isTaskRunning(row) ? '关闭' : '开启' }}
          </el-button>
          <el-button class="report-button" size="small" @click="copyTaskJson(row)">复制JSON</el-button>
          <el-button class="report-button" size="small" @click="handleClone(row)">克隆</el-button>
          <el-button class="report-button" size="small" type="danger" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 任务编辑/创建对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="50%" :before-close="handleDialogClose">
      <el-form :model="formData" label-width="120px" :rules="formRules" ref="formRef">
        <el-form-item label="任务标题" prop="title">
          <el-input v-model="formData.title" />
        </el-form-item>
        <el-form-item label="任务类型" prop="type">
          <el-select v-model="formData.type" @change="handleTypeChange">
            <el-option v-for="item in taskTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="运行周期" prop="cron_cycle_type">
          <el-checkbox-group v-model="formData.cron_cycle_type">
            <el-checkbox label="day">日</el-checkbox>
            <el-checkbox label="week">周</el-checkbox>
            <el-checkbox label="month">月</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="开始时间" prop="start_date">
          <el-date-picker v-model="formData.start_date" type="date" placeholder="选择开始时间" value-format="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="结束时间" prop="end_date">
          <el-date-picker v-model="formData.end_date" type="date" placeholder="选择结束时间" value-format="YYYY-MM-DD" />
        </el-form-item>

        <!-- 动态内容区域 -->
        <div v-if="formData.type">
          <component :is="getContentComponent(formData.type)" v-model="formData.content" :templateList="templateList" :key="formData.type" />
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import {
  getTaskList,
  getTemplateList,
  createTask,
  updateTask,
  deleteTask,
  startTask,
  stopTask
} from '../utils/restApi'

// 导入不同类型的内容组件
import TemplateContent from '../components/TaskContent/TemplateContent.vue'
import SqlContent from '../components/TaskContent/SqlContent.vue'
import HttpContent from '../components/TaskContent/HttpContent.vue'
import DataworksContent from '../components/TaskContent/DataworksContent.vue'
import LiveSqlContent from '../components/TaskContent/LiveSqlContent.vue'
import LocalLiveSqlContent from '../components/TaskContent/LocalLiveSqlContent.vue'
import LiveDwContent from '../components/TaskContent/LiveDwContent.vue'
import LiveHttpContent from '../components/TaskContent/LiveHttpContent.vue'

export default {
  components: {
    TemplateContent,
    SqlContent,
    HttpContent,
    DataworksContent,
    LiveSqlContent,
    LocalLiveSqlContent,
    LiveDwContent,
    LiveHttpContent
  },
  setup() {
    // 任务类型映射
    const taskTypeMap = {
      TEMPLATE: '模板任务',
      SQL: 'SQL任务',
      HTTP: 'HTTP任务',
      DATAWORKS: 'DataWorks任务',
      LIVE_SQL: '实时SQL',
      LOCAL_LIVE_SQL: '本地实时SQL',
      LIVE_DW: 'DataWorks实时SQL',
      LIVE_HTTP: 'HTTP实时任务'
    }

    // 任务类型选项
    const taskTypeOptions = Object.entries(taskTypeMap).map(([value, label]) => ({ value, label }))

    // 任务列表
    const taskList = ref([])
    const templateList = ref([])
    const loading = ref(false)
    const searchText = ref('')
    const selectedTypes = ref([])
    const selectedStatus = ref(['running', 'stopped'])

    // 对话框相关
    const dialogVisible = ref(false)
    const dialogTitle = ref('')
    const formRef = ref(null)
    const formData = reactive({
      id: null,
      title: '',
      type: '',
      cron_cycle_type: [],
      start_date: '',
      end_date: '',
      content: null
    })

    // 表单验证规则
    const formRules = {
      title: [{ required: true, message: '请输入任务标题', trigger: 'blur' }],
      type: [{ required: true, message: '请选择任务类型', trigger: 'change' }],
      cron_cycle_type: [{ required: true, message: '请选择运行周期', trigger: 'change' }],
      start_date: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
      end_date: [{ required: true, message: '请选择结束时间', trigger: 'change' }]
    }

    // 获取任务列表
    const fetchTaskList = async () => {
      try {
        loading.value = true
        const res = await getTaskList()
        console.log('taskListtaskList', res)
        taskList.value = res || []
      } catch (error) {
        console.error('获取任务列表失败:', error)
        throw new Error('获取任务列表失败')
      } finally {
        loading.value = false
      }
    }

    // 获取模板列表
    const fetchTemplateList = async () => {
      try {
        const res = await getTemplateList()
        templateList.value = res || []
      } catch (error) {
        console.error('获取模板列表失败:', error)
      }
    }

    // 格式化周期类型
    const formatCycleType = (types) => {
      if (!types) return ''
      return types.split(',').map(t => {
        switch (t) {
          case 'day': return '日'
          case 'week': return '周'
          case 'month': return '月'
          default: return t
        }
      }).join(',')
    }

    // 判断任务是否运行中
    const isTaskRunning = (task) => {
      if (task.status === 'close') return false
      if (!task.status || task.status === '') return true
      return false
    }

    // 获取任务状态文本
    const getStatusText = (task) => {
      if (task.status === 'close') return '已关闭'
      if (!task.status || task.status === '') {
        const days = getDaysToExpire(task)
        return days < 0 ? '已停止' : '运行中'
      }
      return '未知状态'
    }

    // 获取状态标签类型
    const getStatusTagType = (task) => {
      if (task.status === 'close') return 'info'
      if (!task.status || task.status === '') {
        const days = getDaysToExpire(task)
        return days < 0 ? 'warning' : 'success'
      }
      return ''
    }

    // 判断任务是否可以开启
    const canStartTask = (task) => {
      return task.status === 'close' ||
        (getDaysToExpire(task) < 0 && (!task.status || task.status === ''))
    }

    // 根据类型获取内容组件
    const getContentComponent = (type) => {
      switch (type) {
        case 'TEMPLATE': return 'TemplateContent'
        case 'SQL': return 'SqlContent'
        case 'HTTP': return 'HttpContent'
        case 'DATAWORKS': return 'DataworksContent'
        case 'LIVE_SQL': return 'LiveSqlContent'
        case 'LOCAL_LIVE_SQL': return 'LocalLiveSqlContent'
        case 'LIVE_DW': return 'LiveDwContent'
        case 'LIVE_HTTP': return 'LiveHttpContent'
        default: return null
      }
    }

    // 处理类型变化
    const handleTypeChange = (type) => {
      // 根据类型初始化content
      switch (type) {
        case 'TEMPLATE':
          formData.content = null
          break
        case 'SQL':
          formData.content = {
            fileName: '',
            orgId: null,
            titleMap: {},
            sql: '',
            webHookUrl: ''
          }
          break
        case 'HTTP':
          formData.content = {
            url: '',
            method: 'POST',
            headers: {},
            body: {},
            webHookUrl: ''
          }
          break
        case 'DATAWORKS':
        case 'LIVE_SQL':
        case 'LOCAL_LIVE_SQL':
        case 'LIVE_DW':
        case 'LIVE_HTTP':
          formData.content = {
            sql: '',
            webHookUrl: ''
          }
          break
      }
    }

    // 新建任务
    const handleCreate = () => {
      dialogTitle.value = '新建任务'
      Object.assign(formData, {
        id: null,
        title: '',
        type: '',
        cron_cycle_type: [],
        start_date: '',
        end_date: '',
        content: null
      })
      dialogVisible.value = true
    }

    // 编辑任务
    const handleEdit = (row) => {
      dialogTitle.value = '编辑任务'
      let content = typeof row.content === 'string' ? JSON.parse(row.content) : row.content
      // 处理模板任务的content格式
      if (row.type === 'TEMPLATE' && content && typeof content === 'object') {
        content = content.id
      }
      Object.assign(formData, {
        id: row.id,
        title: row.title,
        type: row.type,
        cron_cycle_type: row.cron_cycle_type.split(','),
        start_date: row.start_date,
        end_date: row.end_date,
        content: content || null
      })
      dialogVisible.value = true
    }

    // 开启任务
    const handleStart = async (row) => {
      let loadingInstance = null
      try {
        loadingInstance = ElLoading.service({
          lock: true,
          text: '正在开启任务...',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        await ElMessageBox.confirm(
          `确定要开启任务"${row.title}"吗?`,
          '开启确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        await startTask(row.id)
        ElMessage.success('任务已成功开启')
        await fetchTaskList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('开启任务失败:', error)
          ElMessage.error('开启任务失败')
        }
      } finally {
        if (loadingInstance) {
          loadingInstance.close()
        }
      }
    }

    // 关闭任务
    const handleStop = async (row) => {
      let loadingInstance = null
      try {
        loadingInstance = ElLoading.service({
          lock: true,
          text: '正在关闭任务...',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        await ElMessageBox.confirm(
          `确定要关闭任务"${row.title}"吗?`,
          '关闭确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        await stopTask(row.id)
        ElMessage.success('任务已成功关闭')
        await fetchTaskList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('关闭任务失败:', error)
          ElMessage.error('关闭任务失败')
        }
      } finally {
        if (loadingInstance) {
          loadingInstance.close()
        }
      }
    }

    // 删除任务
    const handleDelete = async (row) => {
      let loadingInstance = null
      try {
        loadingInstance = ElLoading.service({
          lock: true,
          text: '正在删除任务...',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        await ElMessageBox.confirm(
          `确定要删除任务"${row.title}"吗?`,
          '删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        await deleteTask(row.id)
        ElMessage.success('删除成功')
        await fetchTaskList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除任务失败:', error)
          ElMessage.error('删除失败')
        }
      } finally {
        if (loadingInstance) {
          loadingInstance.close()
        }
      }
    }

    // 处理SQL内容中的单引号转义
    const escapeSqlContent = (content) => {
      if (!content) return content

      // 处理JSON格式的SQL内容
      if (typeof content === 'object') {
        if (content.sql) {
          return { ...content, sql: content.sql.replace(/'/g, "''") }
        }
        return content
      }

      // 处理纯文本SQL内容
      if (typeof content === 'string' &&
        (formData.type === 'SQL' ||
          formData.type === 'DATAWORKS' ||
          formData.type === 'LIVE_SQL' ||
          formData.type === 'LOCAL_LIVE_SQL' ||
          formData.type === 'LIVE_DW')) {
        return content.replace(/'/g, "''")
      }

      return content
    }

    // 提交表单
    const handleSubmit = async () => {
      let loadingInstance = null
      try {
        loadingInstance = ElLoading.service({
          lock: true,
          text: '正在保存任务...',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        await formRef.value.validate()

        const taskData = {
          title: formData.title,
          type: formData.type,
          cron_cycle_type: formData.cron_cycle_type.join(','),
          start_date: formData.start_date,
          end_date: formData.end_date,
          content: escapeSqlContent(formData.type === 'TEMPLATE' ? formData.content : formData.content)
        }

        if (formData.id) {
          await updateTask(formData.id, taskData)
        } else {
          await createTask(taskData)
        }

        dialogVisible.value = false
        await fetchTaskList()
      } catch (error) {
        console.error('保存任务失败:', error)
        ElMessage.error('保存任务失败')
      } finally {
        if (loadingInstance) {
          loadingInstance.close()
        }
      }
    }

    // 关闭对话框
    const handleDialogClose = (done) => {
      done()
    }

    // 计算过滤后的任务列表
    const filteredTaskList = computed(() => {
      return taskList.value.filter(task => {
        const matchesSearch = searchText.value === '' ||
          task.title.toLowerCase().includes(searchText.value.toLowerCase())
        const matchesType = selectedTypes.value.length === 0 ||
          selectedTypes.value.includes(task.type)
        const matchesStatus = selectedStatus.value.length === 0 ||
          (selectedStatus.value.includes('running') && isTaskRunning(task)) ||
          (selectedStatus.value.includes('closed') && task.status === 'close') ||
          (selectedStatus.value.includes('stopped') && !isTaskRunning(task) && task.status !== 'close')
        return matchesSearch && matchesType && matchesStatus
      })
    })

    // 计算到期天数
    const getDaysToExpire = (task) => {
      if (!task.end_date) return Infinity
      // if  (task.status === 'close') return -999;
      const endDate = new Date(task.end_date)
      const now = new Date()
      const diffTime = endDate - now
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }

    // 获取到期标签类型
    const getExpireTagType = (task) => {
      const days = getDaysToExpire(task)
      if (days <= 3) return 'danger'
      if (days <= 9) return 'warning'
      return 'success'
    }

    // 获取到期文本
    const getExpireText = (task) => {
      if (task.status === 'close') return ''
      if (!isTaskRunning(task)) return ''
      const days = getDaysToExpire(task)
      if (days < 0) return '到期已自动停止'
      if (days > 30) return `应该还早：${days}天`
      return `${days}天后到期`
    }

    // 克隆任务
    const handleClone = (task) => {
      dialogTitle.value = '克隆任务'
      let content = task.content
      try {
        // 尝试解析JSON格式的content
        if (typeof task.content === 'string') {
          try {
            content = JSON.parse(task.content)
          } catch {
            // 如果不是JSON格式，保持原样
            content = task.content
          }
        }
        // 处理模板任务的content格式
        if (task.type === 'TEMPLATE' && content && typeof content === 'object') {
          content = content.id
        }
        Object.assign(formData, {
          id: null,
          title: `${task.title}(克隆)`,
          type: task.type,
          cron_cycle_type: task.cron_cycle_type.split(','),
          start_date: task.start_date,
          end_date: task.end_date,
          content: content || null
        })
        dialogVisible.value = true
      } catch (error) {
        console.error('克隆任务失败:', error)
        ElMessage.error('克隆任务失败，请检查数据格式')
      }
    }

    // 降级复制方案
    const fallbackCopyText = (text) => {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'  // 防止页面滚动
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        ElMessage.success('已复制到剪贴板')
      } catch (err) {
        ElMessage.error('复制失败，请手动复制')
      } finally {
        document.body.removeChild(textarea)
      }
    }

    // 复制任务JSON
    const copyTaskJson = (task) => {
      try {
        let content = task.content
        // 如果content是字符串，尝试解析为JSON
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content)
          } catch {
            // 如果不是JSON格式，保持原样
          }
        }

        // 如果是对象，格式化为JSON字符串
        const textToCopy = typeof content === 'object'
          ? JSON.stringify(content, null, 2)
          : content

        // 现代浏览器API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(textToCopy)
            .then(() => {
              ElMessage.success('已复制到剪贴板')
            })
            .catch(() => {
              // 降级方案
              fallbackCopyText(textToCopy)
            })
        } else {
          // 旧浏览器降级方案
          fallbackCopyText(textToCopy)
        }
      } catch (error) {
        console.error('复制失败:', error)
        ElMessage.error('复制失败')
      }
    }

    // 原始任务列表备份
    const originalTaskList = ref([])

    // 初始化数据
    onMounted(() => {
      init();
    })
    async function init() {
      await fetchTaskList().then(() => {
        originalTaskList.value = [...taskList.value]
      })
      await fetchTemplateList();
    }

    // 处理排序
    const handleSortChange = ({ prop, order }) => {
      if (!order) {
        // 取消排序时恢复原始顺序
        taskList.value = [...originalTaskList.value]
        return
      }

      taskList.value.sort((a, b) => {
        // 特殊处理状态排序
        if (prop === 'status') {
          const aStatus = isTaskRunning(a) ? 1 : 0
          const bStatus = isTaskRunning(b) ? 1 : 0
          return order === 'ascending' ? aStatus - bStatus : bStatus - aStatus
        }

        // 特殊处理到期时间排序
        if (prop === 'end_date' || prop === 'expire_days') {
          const aDays = getDaysToExpire(a)
          const bDays = getDaysToExpire(b)
          // 已过期的排最后
          if (aDays < 0 && bDays >= 0) return 1
          if (aDays >= 0 && bDays < 0) return -1
          if (aDays < 0 && bDays < 0) return 0
          // 确保排序稳定
          const diff = aDays - bDays
          return order === 'ascending' ? diff : -diff
        }

        // 默认排序
        const aVal = a[prop]
        const bVal = b[prop]
        if (aVal === bVal) return 0
        return order === 'ascending' ?
          (aVal > bVal ? 1 : -1) :
          (aVal < bVal ? 1 : -1)
      })
    }

    return {
      searchText,
      selectedTypes,
      selectedStatus,
      filteredTaskList,
      getDaysToExpire,
      getExpireTagType,
      getExpireText,
      copyTaskJson,
      handleSortChange,
      taskList,
      templateList,
      dialogVisible,
      dialogTitle,
      formRef,
      formData,
      formRules,
      taskTypeMap,
      taskTypeOptions,
      fetchTaskList,
      formatCycleType,
      isTaskRunning,
      getStatusText,
      getStatusTagType,
      canStartTask,
      getContentComponent,
      handleTypeChange,
      handleCreate,
      handleEdit,
      handleStart,
      handleStop,
      handleDelete,
      handleSubmit,
      handleDialogClose,
      handleClone
    }
  }
}
</script>

<style scoped>
.report-button {
  margin-left: 2px !important;
  margin-right: 2px !important;
  min-width: auto !important; /*防止按钮宽度被撑开*/
}
.text-ellipsis {
  display: inline-block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expired-text {
  color: #f56c6c;
}

.early-text {
  color: #67c23a;
}

.el-table >>> .el-table__cell {
  text-align: center;
}

.el-table >>> .el-table__header .el-table__cell {
  text-align: center;
}
.cycle-task-container {
  padding: 20px;
}
.operation-area {
  margin-bottom: 20px;
}
.pagination-area {
  margin-top: 20px;
  text-align: right;
}
</style>
